/* eslint-disable max-len */
import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import { getUrlSearchParams } from 'utils/apiUtils';
import {
  CreateViewRequest,
  JobForMapsPayload,
  JobForMapsResponse,
  MapInitResponse,
  TeamView,
  TeamViewsResponse,
  UpdateJobAddressRequest,
  UpdateViewPriorityRequest,
  UpdateViewRequest,
  UpdateViewResponse
} from './dispatch.model';
import { TeamIdPayload } from 'containers/home/home.model';
import { RootState } from 'store/reducers';
import { selectCurrentActiveView, setCurrentActiveView, setCurrentViewFilters } from './dispatchSlice';
import { getViewFiltersFromTeamView } from './utils';

export const dispatchApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    initializeMap: builder.query<MapInitResponse, JobForMapsPayload>({
      query: ({ teamId, params }) => {
        const urlParams = getUrlSearchParams(params);
        const queryString = urlParams.toString();

        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/maps/init${
            queryString ? `?${queryString}` : ''
          }`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    getJobsForMap: builder.query<JobForMapsResponse, JobForMapsPayload>({
      query: filters => {
        const { teamId, params } = filters;
        const urlParams = getUrlSearchParams({ ...params, includeCount: false });
        const queryString = urlParams.toString();

        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/maps/jobs${
            queryString ? `?${queryString}` : ''
          }`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      providesTags: [API_TAG_TYPES.GET_JOBS_FOR_MAP],
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER
    }),
    getTeamViews: builder.query<TeamViewsResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views?type=map`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      }),
      async onQueryStarted({ view: activeView }, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;
        if (!data) return;

        if (activeView) return;

        const existingActiveView = selectCurrentActiveView(getState() as RootState);
        if (existingActiveView) {
          const refreshedView = data.data.find(view => view.id === existingActiveView.id);
          if (refreshedView) {
            dispatch(setCurrentActiveView(refreshedView));
          }
          return;
        }

        let activeTeamView = data.data.find(view => view.isLastViewed);
        if (!activeTeamView) {
          activeTeamView = data.data.find(view => view.priority === 1);
        }
        dispatch(setCurrentActiveView(activeTeamView));
        dispatch(setCurrentViewFilters(getViewFiltersFromTeamView(activeTeamView)));
      },
      providesTags: [API_TAG_TYPES.TEAM_VIEWS],
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER
    }),
    getTeamViewById: builder.query<any, { teamId: number; viewId: number }>({
      query: ({ teamId, viewId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      })
    }),
    createView: builder.mutation<{ data: TeamView }, CreateViewRequest>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views`,
        method: 'POST',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS],
      extraOptions: {
        failure: 'Some error occurred, view not created',
        success: 'View created successfully',
        showToast: true
      }
    }),
    updateView: builder.mutation<UpdateViewResponse, { teamId: string; viewId: string; body: UpdateViewRequest }>({
      query: ({ teamId, viewId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS],
      extraOptions: {
        failure: 'Some error occurred, view not updated',
        success: 'View updated successfully',
        showToast: true
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data) {
          dispatch(setCurrentActiveView(data.data));
        }
      }
    }),
    markViewAsLastViewed: builder.mutation<void, { teamId: number; viewId: number }>({
      query: ({ teamId, viewId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}/last-viewed?type=map`,
        method: 'PATCH'
      })
    }),
    deleteView: builder.mutation<void, { teamId: number; viewId: number }>({
      query: ({ teamId, viewId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}`,
        method: 'DELETE',
        headers: JOB_MANAGER_API_HEADERS
      })
    }),
    updateViewPriority: builder.mutation<void, UpdateViewPriorityRequest>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/priority?type=map`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      async onQueryStarted({ teamId, body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dispatchApi.util.updateQueryData('getTeamViews', { teamId }, draft => {
            if (!draft?.data) return;

            draft.data.forEach(view => {
              const priorityIndex = body.viewIds.indexOf(view.id);
              if (priorityIndex >= 0) {
                view.isEnabled = true;
                view.priority = priorityIndex + 1;
              } else {
                view.isEnabled = false;
              }
            });

            draft.data.sort((a, b) => {
              if (a.isEnabled !== b.isEnabled) {
                return a.isEnabled ? -1 : 1;
              }

              return a.priority - b.priority;
            });
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    getTeamEvents: builder.query<any, { teamId: number; clientId: string }>({
      query: ({ teamId, clientId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/events/${teamId}?client_id=${clientId}`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      })
    }),
    updateJobAddress: builder.mutation<void, UpdateJobAddressRequest>({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${payload.teamId}/jobs/${payload.jobId}/address`,
        method: 'PATCH',
        body: payload,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        failure: 'Failed to update job address.',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetJobsForMapQuery,
  useLazyGetTeamViewsQuery,
  useLazyGetTeamViewByIdQuery,
  useUpdateViewMutation,
  useCreateViewMutation,
  useDeleteViewMutation,
  useMarkViewAsLastViewedMutation,
  useUpdateViewPriorityMutation,
  useLazyInitializeMapQuery,
  useLazyGetTeamEventsQuery,
  useGetTeamEventsQuery,
  useUpdateJobAddressMutation,
  useGetJobsForMapQuery
} = dispatchApi;
